// @ts-nocheck
/**
 * Storage Manager Component
 * Manages file uploads, downloads, and organization for educational content
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Button,
  FileUploader,
  Modal,
  ProgressIndicator,
  ProgressStep,
  Tag,
  Tile,
  InlineNotification,
  Dropdown,
  Pagination,
  OverflowMenu,
  OverflowMenuItem,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
} from '@carbon/react';
import {
  Upload,
  Download,
  Delete,
  View,
  Filter,
  FolderAdd,
  Document,
  Video,
  Image,
  Archive,
  CloudUpload,
} from '@carbon/icons-react';
import { useAuthStore } from '../../store/authStore';
import axios from 'axios';
import { formatFileSize, formatDate } from '../../utils/formatters';

// Configure API client
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds for file operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(config => {
  const sessionToken = sessionStorage.getItem('access_token');
  const localToken = localStorage.getItem('access_token');
  const token = sessionToken || localToken;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

interface FileMetadata {
  file_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  uploaded_by: string;
  family_id?: string;
  class_id?: string;
  category: 'document' | 'video' | 'image' | 'assignment' | 'resource';
  tags?: string[];
  is_public: boolean;
  cdn_url?: string;
  thumbnail_url?: string;
  created_at: string;
  updated_at: string;
}

interface UploadProgress {
  fileId: string;
  fileName: string;
  progress: number;
  status: 'preparing' | 'uploading' | 'processing' | 'complete' | 'error';
  error?: string;
}

interface StorageAnalytics {
  categoryStats: Array<{
    category: string;
    file_count: number;
    total_size: number;
    avg_size: number;
  }>;
  totalStats: {
    total_files: number;
    total_storage: number;
    unique_uploaders: number;
  };
  storageLimit: string;
  usagePercentage: number;
}

const FILE_CATEGORIES = [
  { value: 'document', label: 'Documents', icon: Document },
  { value: 'video', label: 'Videos', icon: Video },
  { value: 'image', label: 'Images', icon: Image },
  { value: 'assignment', label: 'Assignments', icon: Document },
  { value: 'resource', label: 'Resources', icon: Archive },
];

const FILE_SIZE_LIMITS = {
  document: 50 * 1024 * 1024,
  video: 500 * 1024 * 1024,
  image: 10 * 1024 * 1024,
  assignment: 50 * 1024 * 1024,
  resource: 100 * 1024 * 1024,
};

export const StorageManager: React.FC = () => {
  const { user } = useAuthStore();
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [analytics, setAnalytics] = useState<StorageAnalytics | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Load files and analytics
  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });

      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }

      if (user?.familyId && user.role !== 'admin') {
        params.append('familyId', user.familyId);
      }

      const response = await api.get(`/v1/storage/files?${params.toString()}`);
      setFiles(response.data.files);
      setTotalItems(response.data.pagination.total);
    } catch (err) {
      setError('Failed to load files');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, selectedCategory, user]);

  const loadAnalytics = useCallback(async () => {
    try {
      const response = await api.get('/v1/storage/analytics');
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  }, []);

  useEffect(() => {
    loadFiles();
    loadAnalytics();
  }, [loadFiles, loadAnalytics]);

  // Handle file upload
  const handleFileUpload = async (files: FileList) => {
    const uploadTasks = Array.from(files).map(async file => {
      const fileId = `temp_${Date.now()}_${Math.random()}`;

      // Add to progress tracking
      setUploadProgress(prev => [
        ...prev,
        {
          fileId,
          fileName: file.name,
          progress: 0,
          status: 'preparing',
        },
      ]);

      try {
        // Determine category based on MIME type
        let category: string = 'resource';
        if (file.type.startsWith('image/')) category = 'image';
        else if (file.type.startsWith('video/')) category = 'video';
        else if (file.type.includes('pdf') || file.type.includes('document') || file.type.includes('word')) {
          category = 'document';
        }

        // Check file size
        const sizeLimit = FILE_SIZE_LIMITS[category as keyof typeof FILE_SIZE_LIMITS];
        if (file.size > sizeLimit) {
          throw new Error(`File size exceeds ${sizeLimit / (1024 * 1024)}MB limit`);
        }

        // Update progress
        setUploadProgress(prev =>
          prev.map(p => (p.fileId === fileId ? { ...p, status: 'uploading', progress: 10 } : p))
        );

        // For large files, use multipart upload
        if (file.size > 10 * 1024 * 1024) {
          // 10MB
          // Request pre-signed URL
          const presignedResponse = await api.post('/v1/storage/upload/presigned', {
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
            category,
            familyId: user?.familyId,
          });

          // Simulate upload progress
          let progress = 10;
          const progressInterval = setInterval(() => {
            progress += Math.random() * 20;
            if (progress > 90) progress = 90;
            setUploadProgress(prev => prev.map(p => (p.fileId === fileId ? { ...p, progress } : p)));
          }, 500);

          // Complete upload
          await api.post('/v1/storage/upload/complete', {
            fileId: presignedResponse.data.fileId,
            uploadId: presignedResponse.data.uploadUrl,
            parts: [], // Would include actual parts in real implementation
          });

          clearInterval(progressInterval);
        } else {
          // Direct upload for smaller files
          const formData = new FormData();
          formData.append('file', file);
          formData.append('category', category);
          formData.append('familyId', user?.familyId || '');

          await api.post('/v1/storage/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: progressEvent => {
              const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
              setUploadProgress(prev => prev.map(p => (p.fileId === fileId ? { ...p, progress } : p)));
            },
          });
        }

        // Mark as complete
        setUploadProgress(prev =>
          prev.map(p => (p.fileId === fileId ? { ...p, status: 'complete', progress: 100 } : p))
        );

        setSuccess(`Successfully uploaded ${file.name}`);
      } catch (err: any) {
        setUploadProgress(prev =>
          prev.map(p =>
            p.fileId === fileId
              ? {
                  ...p,
                  status: 'error',
                  error: err.message || 'Upload failed',
                }
              : p
          )
        );
        setError(`Failed to upload ${file.name}: ${err.message}`);
      }
    });

    await Promise.all(uploadTasks);

    // Reload files after upload
    setTimeout(() => {
      loadFiles();
      loadAnalytics();
      setUploadProgress([]);
      setUploadModalOpen(false);
    }, 2000);
  };

  // Handle file download
  const handleDownload = async (fileId: string, fileName: string) => {
    try {
      const response = await api.get(`/v1/storage/download/${fileId}`);

      if (response.data.downloadUrl) {
        // Open download URL in new tab
        window.open(response.data.downloadUrl, '_blank');
      }

      setSuccess(`Downloading ${fileName}`);
    } catch (err) {
      setError('Failed to download file');
    }
  };

  // Handle file deletion
  const handleDelete = async (fileId: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await api.delete(`/v1/storage/files/${fileId}`);
      setSuccess('File deleted successfully');
      loadFiles();
      loadAnalytics();
    } catch (err) {
      setError('Failed to delete file');
    }
  };

  // Get icon for file category
  const getCategoryIcon = (category: string) => {
    const categoryConfig = FILE_CATEGORIES.find(c => c.value === category);
    return categoryConfig?.icon || Document;
  };

  // Get color for file category
  const getCategoryColor = (category: string): any => {
    const colors: Record<string, any> = {
      document: 'blue',
      video: 'purple',
      image: 'green',
      assignment: 'teal',
      resource: 'gray',
    };
    return colors[category] || 'gray';
  };

  // Filter files based on search
  const filteredFiles = files.filter(
    file =>
      file.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="storage-manager">
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--cds-spacing-07)',
        }}
      >
        <div>
          <h1 className="cds--type-productive-heading-05">Storage Manager</h1>
          <p className="cds--type-body-01" style={{ color: 'var(--cds-text-secondary)' }}>
            Manage educational content and resources
          </p>
        </div>
        <Button kind="primary" renderIcon={Upload} onClick={() => setUploadModalOpen(true)}>
          Upload Files
        </Button>
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--cds-spacing-05)',
            marginBottom: 'var(--cds-spacing-07)',
          }}
        >
          <Tile>
            <p className="cds--type-helper-text-01" style={{ color: 'var(--cds-text-secondary)' }}>
              Total Files
            </p>
            <p className="cds--type-productive-heading-04">{analytics.totalStats.total_files}</p>
          </Tile>
          <Tile>
            <p className="cds--type-helper-text-01" style={{ color: 'var(--cds-text-secondary)' }}>
              Storage Used
            </p>
            <p className="cds--type-productive-heading-04">{formatFileSize(analytics.totalStats.total_storage)}</p>
            <ProgressIndicator
              label=""
              value={analytics.usagePercentage}
              max={100}
              size="small"
              style={{ marginTop: 'var(--cds-spacing-02)' }}
            />
          </Tile>
          <Tile>
            <p className="cds--type-helper-text-01" style={{ color: 'var(--cds-text-secondary)' }}>
              Storage Limit
            </p>
            <p className="cds--type-productive-heading-04">{analytics.storageLimit}</p>
          </Tile>
          <Tile>
            <p className="cds--type-helper-text-01" style={{ color: 'var(--cds-text-secondary)' }}>
              Uploaders
            </p>
            <p className="cds--type-productive-heading-04">{analytics.totalStats.unique_uploaders}</p>
          </Tile>
        </div>
      )}

      {/* Notifications */}
      {error && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
          className="mb-4"
        />
      )}
      {success && (
        <InlineNotification
          kind="success"
          title="Success"
          subtitle={success}
          onCloseButtonClick={() => setSuccess(null)}
          className="mb-4"
        />
      )}

      {/* Files Table */}
      <DataTable
        rows={filteredFiles}
        headers={[
          { key: 'file_name', header: 'Name' },
          { key: 'category', header: 'Type' },
          { key: 'file_size', header: 'Size' },
          { key: 'created_at', header: 'Uploaded' },
          { key: 'actions', header: 'Actions' },
        ]}
        render={({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer>
            <TableToolbar>
              <TableToolbarContent>
                <TableToolbarSearch
                  placeholder="Search files..."
                  persistent
                  onChange={(e: any) => setSearchTerm(e.target.value)}
                />
                <Dropdown
                  id="category-filter"
                  label="All Categories"
                  items={[{ value: 'all', label: 'All Categories' }, ...FILE_CATEGORIES]}
                  selectedItem={{ value: selectedCategory }}
                  onChange={(e: any) => setSelectedCategory(e.selectedItem.value)}
                />
              </TableToolbarContent>
            </TableToolbar>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header: any) => (
                    <TableHeader {...getHeaderProps({ header })} key={header.key}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row: any) => {
                  const file = filteredFiles.find(f => f.file_id === row.id);
                  if (!file) return null;

                  const Icon = getCategoryIcon(file.category);

                  return (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Icon size={20} style={{ marginRight: 'var(--cds-spacing-02)' }} />
                          <div>
                            <p style={{ fontWeight: 500 }}>{file.file_name}</p>
                            {file.tags && file.tags.length > 0 && (
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 'var(--cds-spacing-01)',
                                  marginTop: 'var(--cds-spacing-01)',
                                }}
                              >
                                {file.tags.slice(0, 3).map(tag => (
                                  <Tag key={tag} kind="gray" size="sm">
                                    {tag}
                                  </Tag>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Tag kind={getCategoryColor(file.category)}>{file.category}</Tag>
                      </TableCell>
                      <TableCell>{formatFileSize(file.file_size)}</TableCell>
                      <TableCell>{formatDate(file.created_at)}</TableCell>
                      <TableCell>
                        <OverflowMenu flipped>
                          <OverflowMenuItem
                            itemText="Download"
                            onClick={() => handleDownload(file.file_id, file.file_name)}
                          />
                          {file.cdn_url && (
                            <OverflowMenuItem itemText="View" onClick={() => window.open(file.cdn_url, '_blank')} />
                          )}
                          {(user?.role === 'platform_admin' || file.uploaded_by === user?.userId) && (
                            <OverflowMenuItem
                              itemText="Delete"
                              hasDivider
                              isDelete
                              onClick={() => handleDelete(file.file_id)}
                            />
                          )}
                        </OverflowMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Pagination
              page={currentPage}
              pageSize={pageSize}
              pageSizes={[10, 20, 50, 100]}
              totalItems={totalItems}
              onChange={({ page, pageSize }: any) => {
                setCurrentPage(page);
                setPageSize(pageSize);
              }}
            />
          </TableContainer>
        )}
      />

      {/* Upload Modal */}
      <Modal
        open={uploadModalOpen}
        modalHeading="Upload Files"
        primaryButtonText="Close"
        onRequestClose={() => setUploadModalOpen(false)}
        onRequestSubmit={() => setUploadModalOpen(false)}
        size="lg"
      >
        <div className="p-4">
          <FileUploader
            labelTitle="Upload files"
            labelDescription="Max file size varies by type. Documents: 50MB, Videos: 500MB, Images: 10MB"
            buttonLabel="Add files"
            buttonKind="tertiary"
            accept={[
              '.pdf',
              '.doc',
              '.docx',
              '.txt',
              '.mp4',
              '.webm',
              '.mov',
              '.jpg',
              '.jpeg',
              '.png',
              '.gif',
              '.webp',
              '.zip',
            ]}
            multiple
            onChange={(e: any) => handleFileUpload(e.target.files)}
            iconDescription="Clear file"
            name="file-uploader"
          />

          {/* Upload Progress */}
          {uploadProgress.length > 0 && (
            <div style={{ marginTop: 'var(--cds-spacing-07)' }}>
              <h4
                className="cds--type-body-compact-01"
                style={{ fontWeight: 500, marginBottom: 'var(--cds-spacing-05)' }}
              >
                Upload Progress
              </h4>
              <StructuredListWrapper>
                <StructuredListBody>
                  {uploadProgress.map(upload => (
                    <StructuredListRow key={upload.fileId}>
                      <StructuredListCell>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontWeight: 500 }}>{upload.fileName}</p>
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: 'var(--cds-spacing-02)' }}>
                              <div style={{ flex: 1, marginRight: 'var(--cds-spacing-05)' }}>
                                <ProgressIndicator label="" value={upload.progress} max={100} size="small" />
                              </div>
                              <Tag
                                type={
                                  upload.status === 'complete' ? 'green' : upload.status === 'error' ? 'red' : 'blue'
                                }
                              >
                                {upload.status}
                              </Tag>
                            </div>
                            {upload.error && <p className="text-sm text-red-600 mt-1">{upload.error}</p>}
                          </div>
                        </div>
                      </StructuredListCell>
                    </StructuredListRow>
                  ))}
                </StructuredListBody>
              </StructuredListWrapper>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
